import { z } from 'zod';
import { InstantSchema, JobOrderNoSchema, KitIdSchema } from './common.schema';

/**
 * Server Sent Events, section 7.8, as built in section 24 of the review of
 * 2 October 2026.
 *
 * The client maps each event to a TanStack Query cache invalidation. It does
 * not patch the cache by hand, because a hand patched cache and a refetch
 * disagree the first time an event is missed. That is also why the events are
 * thin: they say THAT something changed and roughly where, never what it now
 * says, and never a dentist's or a patient's name. Each screen refetches what
 * its caller may see.
 *
 * While the stream is open the screens poll once a minute as a safety net.
 * When it is unavailable or drops, they go back to polling every 15 seconds
 * until it reconnects. Nothing here is bidirectional, so there is no WebSocket
 * layer.
 */

export const ConnectedEventSchema = z.object({});

/**
 * A committed save touched a case, an order or the work on one: a clock, a
 * note, a photograph, a hold, a payment, a shipment, a deletion or an archive.
 * Sent to everybody. The ids are a hint; past fifty of either the lists are
 * empty, and an empty list means "refetch everything live".
 */
export const ChangedEventSchema = z.object({
  kitIds: z.array(KitIdSchema),
  jobOrderNos: z.array(JobOrderNoSchema),
  at: InstantSchema,
});

/** The caller's bell changed: a notification arrived, or was read on another device. */
export const NotificationEventSchema = z.object({
  at: InstantSchema,
});

/**
 * The caller's own access changed: role, department, supervisor, exceptions,
 * or the matrix. The app reloads its session, so the change applies on the
 * next action without signing out. Request of 4 October 2026, section 3.
 */
export const SessionEventSchema = z.object({
  at: InstantSchema,
});

export const REALTIME_EVENTS = {
  connected: ConnectedEventSchema,
  changed: ChangedEventSchema,
  notification: NotificationEventSchema,
  session: SessionEventSchema,
} as const;

export type RealtimeEventName = keyof typeof REALTIME_EVENTS;

/** `POST /events/ticket`. Preferred over putting the access token in the URL. */
export const StreamTicketSchema = z.object({
  ticket: z.string(),
  expiresIn: z.number().int().positive(),
});
